/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import { ClockCircleOutlined, LoadingOutlined } from "@ant-design/icons";

import useDebounce from "../hooks/useDebounce";
import {
  createManagePlanPurchase,
  createManagePlanRefund,
  getManagePlanDetails,
  getManagePlanForTierOrder,
  getUserDetailsFromId,
  getUsersWithRefernEarnEnabled,
} from "../Pages/api";
import { TIER_NAME_MAP } from "../Utils/constants";

const getProductName = (product) => {
  switch (product) {
    case "freedom-plan":
      return "freedom";
    case "enterprise-plan":
      return "enterprise";
    default:
      return "tagmango";
  }
};

const DEFAULT_PLAN_DOWNGRADE_DETAILS = {
  hasScheduledDowngrade: false,
  scheduledDowngradeAt: null,
  tierAfterScheduledDowngrade: null,
  tierBeforeScheduledDowngrade: null,
};

// mode = normal | refundingTierOrder | maker-checker
const ManagePlanModal = ({
  visible,
  onClose,
  onSave,
  userId,
  tierOrderId,
  managePlanId,
  mode = "normal",
  editAmountOnly = false,
}) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [partners, setPartners] = useState([]);
  const [txnType, setTxnType] = useState("purchase"); // purchase | refund
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundData, setRefundData] = useState({
    refundOptions: {
      platformSetup: "",
      tier: "",
      suspendCreator: true,
    },
  });
  const [planDowngradeDetails, setPlanDowngradeDetails] = useState({
    ...DEFAULT_PLAN_DOWNGRADE_DETAILS,
  });
  const [userDetails, setUserDetails] = useState(null);
  const [isRevenueShareAutoFilled, setIsRevenueShareAutoFilled] =
    useState(false);

  const [filters] = useState({
    page: 1,
    pageSize: 25,
  });
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });

  const [existingData, setExistingData] = useState({
    productName: "",
    plan: "",
    taggedPartner: null,
    tmCommission: "",
    stripeCommission: "",
    indianPGOnetimeCommission: "",
    indianPGRecurringCommission: "",
  });

  // show activate spark only for tagmango users
  const canShowActivateSpark = useMemo(() => {
    return userDetails?.host && !userDetails?.host?.includes("tagmango.com");
  }, [formValues?.platformSetup, userDetails?.host]);

  // Basic (tier1) is a lifetime plan, so only paid-tier purchases get an expiry.
  const needsTierExpiry =
    txnType === "purchase" && formValues?.tier !== "tier1";

  const totalPrice = useMemo(() => {
    const onetimeFee = Number(formValues?.onetimeFee) || 0;
    const subscriptionFee = Number(formValues?.subscriptionFee) || 0;
    return onetimeFee + subscriptionFee;
  }, [formValues?.onetimeFee, formValues?.subscriptionFee]);

  const getPartnersList = async () => {
    setPartnersLoading(true);
    try {
      const res = await getUsersWithRefernEarnEnabled({
        ...filters,
        search: debouncedSearch,
      });

      if (res?.code === 200) {
        setPartners(res.result.users);
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setPartnersLoading(false);
    }
  };

  const getExistingPlanDetails = async () => {
    setLoading(true);
    try {
      const res = await getUserDetailsFromId(userId);

      if (res?.code === 200) {
        const commonReferralRevenueShare =
          res?.result?.refernEarnParent?.userPrivateInfo
            ?.commonReferralRevenueShare;
        const hasAutoFilledRevenueShare =
          typeof commonReferralRevenueShare === "number";

        form.setFieldsValue({
          platformSetup: getProductName(res?.result?.whitelabelPlanType),
          tier: res?.result?.userTier,
          indianPGOnetimeCommission: res?.result?.razorpayOnetimeComission,
          indianPGRecurringCommission: res?.result?.razorpayRecurringComission,
          activateSpark: !!res?.result?.isSparksEnabled,
          applyGstOnPgCharges: !!res?.result?.applyGstOnPgCharges,
          revenueShare: hasAutoFilledRevenueShare
            ? commonReferralRevenueShare
            : undefined,
        });
        setFormValues({
          platformSetup: getProductName(res?.result?.whitelabelPlanType),
          tier: res?.result?.userTier,
          indianPGOnetimeCommission: res?.result?.razorpayOnetimeComission,
          indianPGRecurringCommission: res?.result?.razorpayRecurringComission,
          activateSpark: !!res?.result?.isSparksEnabled,
          revenueShare: hasAutoFilledRevenueShare
            ? commonReferralRevenueShare
            : undefined,
        });
        setIsRevenueShareAutoFilled(hasAutoFilledRevenueShare);
        setExistingData({
          productName: getProductName(res?.result?.whitelabelPlanType),
          plan: res?.result?.userTier,
          taggedPartner: res?.result?.refernEarnParent
            ? res?.result?.refernEarnParent
            : null,
          indianPGOnetimeCommission: res?.result?.razorpayOnetimeComission,
          indianPGRecurringCommission: res?.result?.razorpayRecurringComission,
        });
        setPlanDowngradeDetails({
          hasScheduledDowngrade:
            res?.result?.userPrivateInfo?.hasScheduledDowngrade || false,
          scheduledDowngradeAt:
            res?.result?.userPrivateInfo?.scheduledDowngradeAt || null,
          tierAfterScheduledDowngrade:
            res?.result?.userPrivateInfo?.tierAfterScheduledDowngrade || null,
          tierBeforeScheduledDowngrade:
            res?.result?.userPrivateInfo?.tierBeforeScheduledDowngrade || null,
        });
        setUserDetails(res?.result);
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const purchasePlan = async (values) => {
    if (mode === "maker-checker") {
      onSave(values, "purchase");
      return;
    }
    setLoading(true);
    try {
      const res = await createManagePlanPurchase(values, userId);

      if (res?.code === 200 || res?.code === 201) {
        message.success("Plan purchased successfully");
        form.resetFields();
        onSave();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const refundPlan = async (values) => {
    if (mode === "maker-checker") {
      onSave(values, "refund");
      return;
    }
    setLoading(true);
    try {
      const res = await createManagePlanRefund(
        {
          ...values,
          ...(mode === "refundingTierOrder"
            ? { refundingTierOrder: tierOrderId }
            : {}),
        },
        userId
      );

      if (res?.code === 200 || res?.code === 201) {
        message.success("Plan Refunded successfully");
        form.resetFields();
        onSave();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      visible &&
      userId &&
      mode !== "refundingTierOrder" &&
      mode !== "maker-checker"
    ) {
      getExistingPlanDetails();
      setScheduledAt(null);
      setSearch("");
      // Load all partners when modal opens (empty search = all partners)
      getPartnersList();
    }

    return () => {
      setPlanDowngradeDetails({ ...DEFAULT_PLAN_DOWNGRADE_DETAILS });
      setSearch("");
      setPartners([]);
      setIsRevenueShareAutoFilled(false);
    };
  }, [visible, userId, mode]);

  useEffect(() => {
    // Reset partners list first to show loading indicator
    setPartners([]);
    // Load partners list when search is empty or has a value
    // Empty search means load all partners
    getPartnersList();
  }, [debouncedSearch]);

  useEffect(() => {
    if (mode === "refundingTierOrder" && tierOrderId) {
      setTxnType("refund");
      getManagePlanForTierOrder(tierOrderId)
        .then((res) => {
          const planDetails = res.result.managePlan;

          form.setFieldsValue({
            platformSetup: planDetails?.platformSetup,
            tier: planDetails?.tier,
            indianPGOnetimeCommission: planDetails?.indianPGOnetimeCommission,
            indianPGRecurringCommission:
              planDetails?.indianPGRecurringCommission,
            onetimeFee: planDetails?.onetimeFee,
            subscriptionFee: planDetails?.subscriptionFee,
            paymentPlan: planDetails?.paymentPlan,
            revenueShare: planDetails?.revenueShare,
            taggedPartner: planDetails?.referredBy,
            remarks: `Refund for order ${tierOrderId}`,
          });
          setFormValues({
            platformSetup: planDetails?.platformSetup,
            tier: planDetails?.tier,
            indianPGOnetimeCommission: planDetails?.indianPGOnetimeCommission,
            indianPGRecurringCommission:
              planDetails?.indianPGRecurringCommission,
            onetimeFee: planDetails?.onetimeFee,
            subscriptionFee: planDetails?.subscriptionFee,
            paymentPlan: planDetails?.paymentPlan,
            revenueShare: planDetails?.revenueShare,
            taggedPartner: planDetails?.referredBy,
            remarks: `Refund for order ${tierOrderId}`,
          });
        })
        .catch((err) => {
          console.log(err);
        });
    } else if (mode === "maker-checker" && managePlanId) {
      Promise.all([
        getManagePlanDetails(managePlanId),
        getUserDetailsFromId(userId),
      ])
        .then(([res, userRes]) => {
          const planDetails = res.result;

          form.setFieldsValue({
            platformSetup: planDetails?.platformSetup,
            tier: planDetails?.tier,
            indianPGOnetimeCommission: planDetails?.indianPGOnetimeCommission,
            indianPGRecurringCommission:
              planDetails?.indianPGRecurringCommission,
            onetimeFee: planDetails?.onetimeFee,
            subscriptionFee: planDetails?.subscriptionFee,
            paymentPlan: planDetails?.paymentPlan,
            revenueShare: planDetails?.revenueShare,
            taggedPartner: planDetails?.referredBy,
            activateSpark: planDetails?.activateSpark,
            applyGstOnPgCharges: !!planDetails?.applyGstOnPgCharges,
            // Older plans have no expiry; the approver must pick one.
            tierExpireAt: planDetails?.tierExpireAt
              ? moment(planDetails.tierExpireAt)
              : undefined,
          });
          setFormValues({
            platformSetup: planDetails?.platformSetup,
            tier: planDetails?.tier,
            indianPGOnetimeCommission: planDetails?.indianPGOnetimeCommission,
            indianPGRecurringCommission:
              planDetails?.indianPGRecurringCommission,
            onetimeFee: planDetails?.onetimeFee,
            subscriptionFee: planDetails?.subscriptionFee,
            paymentPlan: planDetails?.paymentPlan,
            revenueShare: planDetails?.revenueShare,
            taggedPartner: planDetails?.referredBy,
            activateSpark: planDetails?.activateSpark,
            applyGstOnPgCharges: !!planDetails?.applyGstOnPgCharges,
          });
          setExistingData((prev) => ({
            ...prev,
            taggedPartner: planDetails?.referredBy ?? null,
          }));
          setUserDetails(userRes.result);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [mode, tierOrderId, managePlanId, userId]);

  return (
    <>
      <Modal
        title="Manage Plan"
        visible={visible}
        onCancel={onClose}
        destroyOnClose
        footer={[
          <Row style={{ gap: 16 }}>
            <Button
              key="reset"
              type="default"
              onClick={() => {
                form.resetFields();
                onClose();
              }}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
            <div style={{ flex: 1, display: "flex" }}>
              {!scheduledAt &&
              txnType !== "refund" &&
              mode !== "maker-checker" ? (
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  style={{ marginRight: -6 }}
                  disabled={loading}
                  onClick={() => {
                    setScheduleModalVisible(true);
                  }}
                />
              ) : null}
              <Button
                key="submit"
                type="primary"
                loading={loading}
                onClick={() => {
                  form.validateFields().then((values) => {
                    // find if oneTimeFee or subscriptionFee is present
                    // if yes then convert it to number

                    const validatedValues = values;

                    if (values.onetimeFee) {
                      validatedValues.onetimeFee = parseInt(
                        values.onetimeFee,
                        10
                      );
                    } else {
                      validatedValues.onetimeFee = 0;
                    }

                    if (values.subscriptionFee) {
                      validatedValues.subscriptionFee = parseInt(
                        values.subscriptionFee,
                        10
                      );
                    } else {
                      validatedValues.subscriptionFee = 0;
                    }

                    if (values.indianPGOnetimeCommission) {
                      validatedValues.indianPGOnetimeCommission = parseFloat(
                        values.indianPGOnetimeCommission,
                        10
                      );
                    }

                    if (values.indianPGRecurringCommission) {
                      validatedValues.indianPGRecurringCommission = parseFloat(
                        values.indianPGRecurringCommission,
                        10
                      );
                    }

                    if (typeof values.revenueShare !== "number") {
                      validatedValues.revenueShare = 0;
                    }

                    // Only paid-tier purchases have an expiry; Basic is lifetime.
                    if (!needsTierExpiry) {
                      delete validatedValues.tierExpireAt;
                    }

                    if (scheduledAt) {
                      validatedValues.scheduledAt = scheduledAt;
                    }

                    if (existingData.taggedPartner) {
                      validatedValues.referredBy =
                        existingData.taggedPartner._id;
                    }

                    if (txnType === "purchase") {
                      purchasePlan(validatedValues);
                    } else {
                      setRefundData({
                        ...validatedValues,
                        refundOptions: {
                          platformSetup: "",
                          tier: "",
                          suspendCreator: false,
                        },
                      });
                      setRefundModalVisible(true);
                    }
                  });
                }}
                style={{ flex: 1 }}
              >
                {scheduledAt
                  ? "Schedule"
                  : editAmountOnly
                  ? "Update Amount"
                  : mode === "maker-checker"
                  ? "Approve"
                  : txnType === "refund"
                  ? "Continue"
                  : "Save"}
              </Button>
            </div>
          </Row>,
        ]}
      >
        <Spin spinning={loading} tip="Loading...">
          {planDowngradeDetails?.hasScheduledDowngrade && (
            <Alert
              message="Scheduled Plan Downgrade"
              description={
                <div>
                  <Typography.Text>
                    This creator has a scheduled plan downgrade. The plan will
                    be downgraded from{" "}
                    <Typography.Text strong>
                      {TIER_NAME_MAP[
                        planDowngradeDetails?.tierBeforeScheduledDowngrade
                      ] || planDowngradeDetails?.tierBeforeScheduledDowngrade}
                    </Typography.Text>{" "}
                    to{" "}
                    <Typography.Text strong>
                      {TIER_NAME_MAP[
                        planDowngradeDetails?.tierAfterScheduledDowngrade
                      ] || planDowngradeDetails?.tierAfterScheduledDowngrade}
                    </Typography.Text>
                    {planDowngradeDetails?.scheduledDowngradeAt && (
                      <>
                        {" "}
                        on{" "}
                        <Typography.Text strong>
                          {moment(
                            planDowngradeDetails.scheduledDowngradeAt
                          ).format("DD MMM YYYY")}
                        </Typography.Text>
                      </>
                    )}
                    .{" "}
                    <Typography.Text strong>
                      Any plan changes made from here will disrupt this
                      scheduled downgrade.
                    </Typography.Text>
                  </Typography.Text>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: "24px" }}
            />
          )}
          <Form
            form={form}
            disabled={mode === "refundingTierOrder"}
            layout="vertical"
            onValuesChange={(_, values) =>
              setFormValues((prevState) => ({ ...prevState, ...values }))
            }
          >
            <Row style={{ gap: "16px" }}>
              <Form.Item
                style={{ flex: 1 }}
                name="platformSetup"
                label="Product"
                rules={[{ required: true, message: "Please select product" }]}
              >
                <Select
                  disabled={editAmountOnly}
                  onChange={() => {
                    form.setFieldsValue({
                      tier: "tier1",
                    });

                    setFormValues((prevState) => ({
                      ...prevState,
                      tier: "tier1",
                    }));
                  }}
                >
                  <Select.Option value="tagmango">TagMango</Select.Option>
                  <Select.Option value="freedom">Freedom</Select.Option>
                  <Select.Option value="enterprise">Enterprise</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                style={{ flex: 1 }}
                name="tier"
                label="Plan"
                rules={[{ required: true, message: "Please select plan" }]}
              >
                <Select
                  disabled={editAmountOnly}
                  onChange={(value) => {
                    if (value === "tier1") {
                      // remove subscription fee
                      form.setFieldsValue({
                        subscriptionFee: "",
                      });

                      setFormValues((prevState) => ({
                        ...prevState,
                        subscriptionFee: 0,
                      }));
                    }
                  }}
                >
                  <Select.Option value="tier1">Basic</Select.Option>
                  {/* TagMango is Basic only; Freedom and Enterprise both offer
                      the full ladder up to Ultimate */}
                  {formValues?.platformSetup === "tagmango" ? null : (
                    <>
                      <Select.Option value="tier2">Pro</Select.Option>
                      <Select.Option value="tier3">Advance</Select.Option>
                      <Select.Option value="tier4">Ultimate</Select.Option>
                    </>
                  )}
                </Select>
              </Form.Item>
            </Row>

            <Form.Item
              label="Transaction Type"
              rules={[{ required: true, message: "Please select txn type" }]}
            >
              <Select
                onChange={setTxnType}
                value={txnType}
                disabled={mode === "maker-checker" || editAmountOnly}
              >
                <Select.Option value="purchase">Purchase</Select.Option>
                <Select.Option value="refund">Refund</Select.Option>
              </Select>
            </Form.Item>

            {scheduledAt ? (
              <Card
                style={{ marginBottom: "24px" }}
                bodyStyle={{ padding: 12, paddingBottom: 8 }}
              >
                <Row style={{ gap: "12px" }}>
                  <Typography>
                    Activation scheduled on{" "}
                    {moment(scheduledAt).format("DD MMM YYYY")}
                  </Typography>
                  <Typography.Link
                    onClick={() => {
                      setScheduleModalVisible(true);
                    }}
                  >
                    Change
                  </Typography.Link>
                </Row>
              </Card>
            ) : null}

            <Collapse defaultActiveKey={["1"]}>
              <Collapse.Panel header="Payment Details" key="1">
                <>
                  <Form.Item
                    name="onetimeFee"
                    label="One-time fee"
                    rules={[
                      {
                        required: true,
                        message: "Please enter one-time fee",
                      },
                    ]}
                  >
                    <Input prefix="Rs." type="number" placeholder="25000" />
                  </Form.Item>

                  <Row style={{ gap: "16px" }}>
                    <Form.Item
                      style={{ flex: 1 }}
                      name="subscriptionFee"
                      label="Subscription fee"
                      rules={[
                        {
                          required: true,
                          message: "Please enter subscription fee",
                        },
                      ]}
                    >
                      <Input prefix="Rs." type="number" placeholder="25000" />
                    </Form.Item>
                    <Form.Item
                      style={{ flex: 1 }}
                      name="paymentPlan"
                      label="Payment plan"
                    >
                      <Select defaultValue="">
                        <Select.Option value="">None</Select.Option>
                        <Select.Option value="monthly">Monthly</Select.Option>
                        <Select.Option value="quarterly">
                          Quarterly
                        </Select.Option>
                        <Select.Option value="halfyearly">
                          Half-Yearly
                        </Select.Option>
                        <Select.Option value="yearly">Yearly</Select.Option>
                      </Select>
                    </Form.Item>
                  </Row>

                  <Form.Item
                    label="Total fee (One-time fee + Subscription fee)"
                    extra={
                      <span style={{ fontSize: 12 }}>
                        Note: This amount should include GST
                      </span>
                    }
                  >
                    <Input
                      prefix="Rs."
                      type="number"
                      disabled
                      value={totalPrice}
                    />
                  </Form.Item>

                  {/* Ultimate carries the PG commission breakup on both
                      Freedom and Enterprise; TagMango never reaches tier4 */}
                  {["freedom", "enterprise"].includes(
                    formValues?.platformSetup
                  ) && formValues?.tier === "tier4" ? (
                    <>
                      <Card style={{ marginBottom: "24px" }}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Row justify="space-between">
                            <Typography>TM Commission</Typography>
                            <Typography>0%</Typography>
                          </Row>
                          <Row justify="space-between">
                            <Typography>Stripe Commission</Typography>
                            <Typography>5%</Typography>
                          </Row>
                        </Space>
                      </Card>

                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          name="indianPGOnetimeCommission"
                          label="Indian PG charge (One-time)"
                          rules={[
                            {
                              required: true,
                              message: "Please enter a value",
                            },
                          ]}
                        >
                          <Input suffix="%" type="number" placeholder="1.25" />
                        </Form.Item>
                        <Form.Item
                          name="indianPGRecurringCommission"
                          label="Indian PG charge (Recurring)"
                          rules={[
                            {
                              required: true,
                              message: "Please enter a value",
                            },
                          ]}
                        >
                          <Input suffix="%" type="number" placeholder="1.25" />
                        </Form.Item>
                      </Space>
                    </>
                  ) : null}

                  <Form.Item
                    name="revenueShare"
                    label="Revenue share"
                    rules={[
                      {
                        required: !!(
                          existingData?.taggedPartner || formValues?.referredBy
                        ),
                        message: "Please enter revenue share",
                      },
                      {
                        type: "number",
                        min: 0,
                        max: 100,
                        message: "Revenue share must be between 0 and 100",
                      },
                    ]}
                    normalize={(value) => (value ? Number(value) : undefined)}
                    extra={
                      isRevenueShareAutoFilled ? (
                        <span style={{ fontSize: 12 }}>
                          Note: Auto-filled based on existing revenue share. You
                          can edit if needed.
                        </span>
                      ) : undefined
                    }
                  >
                    <Input placeholder="Enter revenue share" type="number" />
                  </Form.Item>
                  <Form.Item name="remarks" label="Remarks">
                    <Input.TextArea placeholder="Enter remarks" />
                  </Form.Item>
                </>
              </Collapse.Panel>
            </Collapse>

            {existingData?.taggedPartner ? (
              <Card style={{ margin: "24px 0" }}>
                <Space size="small" direction="vertical">
                  <Typography.Title level={5}>
                    Tagged Partner Details
                  </Typography.Title>
                  <Typography style={{}}>
                    {existingData?.taggedPartner?.name}
                  </Typography>
                  <Typography>{existingData?.taggedPartner?.email}</Typography>
                  <Typography>{existingData?.taggedPartner?.phone}</Typography>
                  <Typography>({existingData?.taggedPartner?.host})</Typography>
                  <Typography.Link
                    onClick={() => {
                      setExistingData((prevState) => ({
                        ...prevState,
                        taggedPartner: null,
                      }));
                      form.setFieldsValue({ revenueShare: undefined });
                      setFormValues((prev) => ({
                        ...prev,
                        revenueShare: undefined,
                      }));
                      setIsRevenueShareAutoFilled(false);
                    }}
                  >
                    Change
                  </Typography.Link>
                </Space>
              </Card>
            ) : mode === "refundingTierOrder" ? null : (
              <Form.Item
                style={{ marginTop: "24px" }}
                name="referredBy"
                label="Tagged Partner"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  searchValue={search}
                  disabled={editAmountOnly}
                  onSearch={(value) => {
                    setSearch(value);
                  }}
                  onDropdownVisibleChange={(open) => {
                    if (!open) {
                      // Reset search when dropdown closes, but keep partners list
                      setSearch("");
                    }
                  }}
                  onChange={(partnerId) => {
                    const selectedPartner = partners.find(
                      (p) => p._id === partnerId
                    );
                    const partnerRevenueShare =
                      selectedPartner?.commonReferralRevenueShare;
                    const nextRevenueShare =
                      typeof partnerRevenueShare === "number"
                        ? partnerRevenueShare
                        : undefined;

                    form.setFieldsValue({ revenueShare: nextRevenueShare });
                    setFormValues((prev) => ({
                      ...prev,
                      revenueShare: nextRevenueShare,
                    }));
                    setIsRevenueShareAutoFilled(
                      typeof nextRevenueShare === "number"
                    );
                  }}
                  notFoundContent={
                    partnersLoading ? (
                      <LoadingOutlined />
                    ) : search ? (
                      "No partners found"
                    ) : (
                      "No partners available"
                    )
                  }
                  options={partners.map((partner) => ({
                    value: partner._id,
                    label: `${partner.name} (${partner.email}) - ${partner.host}`,
                  }))}
                />
              </Form.Item>
            )}

            {txnType === "purchase" ? (
              <>
                {canShowActivateSpark ? (
                  <Row
                    justify="space-between"
                    align="middle"
                    style={{ marginBottom: 24 }}
                  >
                    <Typography>Activate Spark</Typography>
                    <Form.Item
                      name="activateSpark"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch disabled={editAmountOnly} />
                    </Form.Item>
                  </Row>
                ) : null}

                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: 24 }}
                >
                  <Typography>Apply GST on PG charges</Typography>
                  <Form.Item
                    name="applyGstOnPgCharges"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch disabled={editAmountOnly} />
                  </Form.Item>
                </Row>

                {needsTierExpiry ? (
                  <Row
                    justify="space-between"
                    align="middle"
                    style={{ marginBottom: 24 }}
                  >
                    <Typography>Enable tier expiry date</Typography>
                    {/* Always on: every paid-tier purchase must set a tier expiry date. */}
                    <Switch checked disabled />
                  </Row>
                ) : null}
              </>
            ) : null}

            {needsTierExpiry ? (
              <Form.Item
                name="tierExpireAt"
                label="Valid till"
                rules={[
                  {
                    required: true,
                    message: "Please select a tier expiry date",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current < moment().add(1, "d").startOf("day")
                  }
                  allowClear
                  format="DD MMM YYYY"
                  onChange={(date) =>
                    form.setFieldsValue({
                      expireAt: date?.endOf("day"),
                    })
                  }
                />
              </Form.Item>
            ) : null}
          </Form>
        </Spin>
      </Modal>

      <Modal
        title="Schedule Activation"
        visible={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        onOk={() => {
          setScheduleModalVisible(false);
        }}
        destroyOnClose
        zIndex={2000}
      >
        <Card style={{ marginBottom: "24px" }}>
          <Typography.Title level={5}>Current Plan Details</Typography.Title>
          <Row style={{ gap: "16px" }}>
            <Select disabled value={existingData?.productName}>
              <Select.Option value="tagmango">TagMango</Select.Option>
              <Select.Option value="freedom">Freedom</Select.Option>
              <Select.Option value="enterprise">Enterprise</Select.Option>
            </Select>

            <Select disabled value={existingData?.plan}>
              <Select.Option value="tier1">Basic</Select.Option>
              <Select.Option value="tier2">Pro</Select.Option>
              <Select.Option value="tier3">Advance</Select.Option>
              <Select.Option value="tier4">Ultimate</Select.Option>
            </Select>
          </Row>
        </Card>

        <Card>
          <Typography.Title level={5}>Schedule Plan Details</Typography.Title>
          <Row style={{ gap: "16px" }}>
            <Select disabled value={formValues?.platformSetup}>
              <Select.Option value="tagmango">TagMango</Select.Option>
              <Select.Option value="freedom">Freedom</Select.Option>
              <Select.Option value="enterprise">Enterprise</Select.Option>
            </Select>

            <Select disabled value={formValues?.tier}>
              <Select.Option value="tier1">Basic</Select.Option>
              <Select.Option value="tier2">Pro</Select.Option>
              <Select.Option value="tier3">Advance</Select.Option>
              <Select.Option value="tier4">Ultimate</Select.Option>
            </Select>
          </Row>

          <Typography.Title level={5} style={{ marginTop: "16px" }}>
            Activation Date
          </Typography.Title>
          <DatePicker
            style={{ width: "100%" }}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            disabledDate={(current) =>
              current && current < moment().add(1, "d").startOf("day")
            }
            value={scheduledAt}
            allowClear
            format="DD MMM YYYY"
            onChange={(date) =>
              setScheduledAt(date ? date.startOf("day") : null)
            }
          />
        </Card>
      </Modal>

      <Modal
        title="Refund Confirmation"
        visible={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          setRefundData({
            refundOptions: {
              platformSetup: "",
              tier: "",
              suspendCreator: true,
            },
          });
        }}
        destroyOnClose
        zIndex={3000}
        footer={[
          <Row style={{ gap: 16 }}>
            <Button
              key="reset"
              type="default"
              onClick={() => {
                setRefundModalVisible(false);
              }}
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                setRefundModalVisible(false);
                refundPlan(refundData);
              }}
              style={{ flex: 1 }}
              disabled={
                (!refundData?.refundOptions?.platformSetup ||
                  !refundData?.refundOptions?.tier) &&
                !refundData?.refundOptions?.suspendCreator
              }
            >
              Yes, refund
            </Button>
          </Row>,
        ]}
      >
        <Typography.Title level={5}>Refund Option</Typography.Title>
        <Card style={{ marginBottom: "24px" }}>
          <Row style={{ gap: "16px" }} justify="space-between">
            <Typography.Title level={5}>Platform Access</Typography.Title>
            <Switch
              checked={
                refundData?.refundOptions?.platformSetup &&
                refundData?.refundOptions?.tier
              }
              onClick={() => {
                message.info(
                  "Please select the values from dropdown to change this"
                );
              }}
            />
          </Row>
          <Typography style={{ marginTop: "8px" }}>
            Upon refunding the creator would on the following plan
          </Typography>
          <Row style={{ gap: "16px", marginTop: "16px" }}>
            <Select
              allowClear
              value={refundData?.refundOptions?.platformSetup}
              dropdownStyle={{
                zIndex: 3000,
              }}
              style={{ width: 120 }}
              onChange={(value) => {
                setRefundData({
                  ...refundData,
                  refundOptions: {
                    ...refundData?.refundOptions,
                    platformSetup: value,
                    tier: value === "tagmango" ? "tier1" : "",
                    suspendCreator: false,
                  },
                });
              }}
              options={[
                {
                  value: "tagmango",
                  label: "TagMango",
                },
                {
                  value: "freedom",
                  label: "Freedom",
                },
                {
                  value: "enterprise",
                  label: "Enterprise",
                },
              ]}
            />

            <Select
              allowClear
              style={{ width: 120 }}
              value={refundData?.refundOptions?.tier || ""}
              dropdownStyle={{
                zIndex: 3000,
              }}
              onChange={(value) => {
                setRefundData({
                  ...refundData,
                  refundOptions: {
                    ...refundData?.refundOptions,
                    tier: value,
                    suspendCreator: false,
                  },
                });
              }}
              options={[
                {
                  value: "tier1",
                  label: "Basic",
                },
                ...(refundData?.refundOptions?.platformSetup === "tagmango"
                  ? []
                  : [
                      {
                        value: "tier2",
                        label: "Pro",
                      },
                      {
                        value: "tier3",
                        label: "Advance",
                      },
                      {
                        value: "tier4",
                        label: "Ultimate",
                      },
                    ]),
              ]}
            />
          </Row>
        </Card>

        <Card style={{ marginBottom: "24px" }}>
          <Row style={{ gap: "16px" }} justify="space-between">
            <Typography.Title level={5}>Suspend Creator</Typography.Title>
            <Switch
              checked={
                refundData?.refundOptions?.suspendCreator ||
                refundData?.refundOptions?.platformSetup === "tagmango"
              }
              onChange={() => {
                const isForcedSuspension =
                  refundData?.refundOptions?.platformSetup === "tagmango";

                if (isForcedSuspension) return;

                setRefundData({
                  ...refundData,
                  refundOptions: {
                    platformSetup: "",
                    tier: "",
                    suspendCreator: !refundData?.refundOptions?.suspendCreator,
                  },
                });
              }}
            />
          </Row>

          <Typography style={{ marginTop: "8px" }}>
            {refundData?.refundOptions?.platformSetup === "tagmango"
              ? "You are downgrading the creator from Whitelable setup to TagMango, suspension of this creator's platform is mandatory."
              : "Enabling this would suspend the creator upon refunding"}
          </Typography>
        </Card>

        <Typography.Title level={5}>
          Are you sure you want to refund?
        </Typography.Title>
      </Modal>
    </>
  );
};
ManagePlanModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  userId: PropTypes.string,
  tierOrderId: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  managePlanId: PropTypes.string,
  editAmountOnly: PropTypes.bool,
};

export default ManagePlanModal;
